import React, { useState, useEffect, useMemo } from "react";
import { navigate, useLocation } from "@reach/router";
import moment from "moment";
import { baseUrl, generateBlogCategoryUrl } from "../../core/paths";
import logoImg from "../../images/logo.svg";
import logoVerticalImg from "../../images/logo-mini-vertical.svg";
import { PageCategory, Tag } from "../../core/types";
import Temperature from "../Temperature/Temperature";
import {
  Box,
  Container,
  Flex,
  HStack,
  Link,
  Text,
  Image,
  Grid,
  useMediaQuery,
  keyframes, Spinner, useDisclosure,
} from "@chakra-ui/react";
import { InstagramIcon } from "@components/Icons/InstagramIcon";
import { ShopIcon } from "@components/Icons/ShopIcon";
import { FacebookIcon } from "@components/Icons/FacebookIcon";
import { MenuCard } from "@components/Cards/MenuCard/MenuCard";
import { FundacjaIcon } from "@components/Icons/FundacjaIcon";
import { CalculatorsIcon } from "@components/Icons/CalculatorsIcon";
import { ChevronLeftIcon, CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import { Link as GatsbyLink } from 'gatsby';
import { Search } from "./search";
import { CategoriesMenu } from "./categoriesMenu";
import { WindowSize } from "../../enums/windowSize";
import { Cards } from "./cards";
import { SearchResultPage } from "src/views/Blog/SearchResultPage";

moment.locale("pl");
interface MainMenuProps {
  categories: PageCategory[];
  tags: Tag[];
  metaTitle: string;
}

const SearchQuery = {
  encode(value) {
    return value;
  },
  decode(value) {
    return value;
  },
};

const SocialMediaItem: React.FC<{
  avatar: string;
  label: string;
  url: string;
  media: "instagram" | "facebook";
}> = ({ avatar, label, url, media }) => (
  <a
    href={url}
    className="flex space-x-2.5 items-center p-4 bg-white border-b border-solid border-grey-4 last:border-b-0"
  >
    <img src={avatar} alt="avatar" className="h-8 w-8 rounded-full" />
    <div>
      <div className="text-xs text-grey-3 font-normal capitalize mb-0.5">
        {media}
      </div>
      <div className="text-s font-bold">{label}</div>
    </div>
  </a>
);

export const MainMenu: React.FC<MainMenuProps> = ({ categories = [], tags = [], metaTitle = '' }) => {
  const [isDesktop] = useMediaQuery("(min-width: 1280px)");
  const [isMobile, setIsMobile] = useState(undefined);
  const [isAutofocus, setIsAutofocus] = useState(false);
  const [mediaQuery] = useMediaQuery("(max-width: 48em)");

  useEffect(() => {
    if(mediaQuery !== isMobile) {
      setIsMobile(mediaQuery);
    }
  }, [mediaQuery]);

  const [scroll, handleScroll] = useState(typeof document !== "undefined" ? document.body.getBoundingClientRect().top : 0);
  const [showSearch, handleShowSearch] = useState(true);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInstagramVisible, setIsInstagramVisible] = useState(false);
  const [isFacebookVisible, setIsFacebookVisible] = useState(false);
  const [tempSearchValue, setTempSearchValue] = useState(false);

  // const [searchQuery, setSearchQuery] = useQueryParam("search", SearchQuery);
  const [searchQuery, changeSearchQuery] = useState('');

  const { pathname } = useLocation();

  const setSearchQuery = (text: string) => {
    if(text.trim().length <= 3) {
      setIsAutofocus(true)
    }
    changeSearchQuery(text)
  }

  const mainCategories = categories.filter(({ parent, name }) => !parent && !['Bez kategorii', 'MGnews', 'PloteRP', 'TRUDNE PYTANIA'].includes(name));
  const additionalCategories = categories.filter(({ name }) => ['MGnews', 'PloteRP', 'TRUDNE PYTANIA'].includes(name));
  const additionalCategoriesNotHome = categories.filter(({ name }) => ['TRUDNE PYTANIA'].includes(name));

  useEffect(() => {
    if(typeof window !== "undefined") {
      window.addEventListener('scroll', checkScroll)
      return () => window.removeEventListener('scroll', checkScroll);
    }
  }, [showSearch, scroll])

  const checkScroll = () => {
    const currentScrollPos = window.pageYOffset;
    const top = document.body.getBoundingClientRect().top;
    if(top > 0 && !showSearch) {
      handleShowSearch(true)
    } else if(top > 0 && showSearch) {
    } else if (currentScrollPos < scroll && !showSearch) {
      handleShowSearch(true)
    } else if(currentScrollPos > scroll && showSearch) {
      handleShowSearch(false)
    }
    handleScroll(currentScrollPos);
  }

  const isHome = pathname === "/";

  const mainCategoriesOrdered = useMemo(() => mainCategories.sort((a, b) => new Date(a.categoryConfig.createdAt).getTime() - new Date(b.categoryConfig.createdAt).getTime()), [mainCategories]);

  enum MENU_TYPE {
    MOBILE_HOME,
    MOBILE_NOT_HOME,
    DESKTOP_HOME,
    DESKTOP_NOT_HOME,
    LOADING,
  }

  const goPrev = () => {
    navigate(-1)
  }

  const typeOptions = {
    [MENU_TYPE.LOADING]: () => (
      <>
        <Flex justifyContent="center" md={{'-webkit-backface-visibility': 'hidden'}} webkit w="100%" background="white" p="10px" position="fixed" top="0" left="0">
          <Spinner/>
        </Flex>
      </>
    ),
    [MENU_TYPE.MOBILE_HOME]: () => (
      <>
        <Box md={{'-webkit-backface-visibility': 'hidden'}} w="100%" background="white" p="10px" position="fixed" top="0" left="0">
          <Flex alignItems="center" paddingInlineStart="1rem" justifyContent="space-between">
            <Box >
              <GatsbyLink  to={baseUrl}><Image minH="30px" src={logoVerticalImg}  alt="" /></GatsbyLink>
            </Box>
            <Box>
              {/*<Temperature />*/}
              <Text fontSize="0.5em" color="gray">
                {moment().format("D MMM Y")}
              </Text>
            </Box>
            <Cards background="transparent" isHome={isHome}/>
          </Flex>
          {
            showSearch && (
              <Flex gridGap="20px" alignItems="center">
                <Box w="100%" gridArea="search" alignItems="center" justifyContent="flex-start">
                  <Search autoFocus={isAutofocus} searchQuery={searchQuery} onSearch={setSearchQuery} />
                </Box>
                <CategoriesMenu isMobile={isMobile} windowSize={WindowSize.Mobile} additionalCategories={additionalCategories} mainCategories={mainCategories} tags={tags} />
              </Flex>
            )
          }
        </Box>
        <Box mt="130px"/>
        {
          searchQuery.trim() !== '' && searchQuery.length > 2 && (
            <SearchResultPage searchQuery={searchQuery} onSearch={setSearchQuery} isMobile={isMobile} />
          )
        }
      </>
    ),
    [MENU_TYPE.MOBILE_NOT_HOME]: () => (
      <Flex p="10px 14px" justifyContent="space-betweeen">
        <Flex gridGap="15px" w="100%" gridArea="search" alignItems="center" justifyContent="flex-start">
          <ChevronLeftIcon cursor="pointer" fontSize="1em" onClick={goPrev}/>
          <GatsbyLink  to={baseUrl}><Image src={logoVerticalImg} minH="30px" alt="" /></GatsbyLink>
        </Flex>
        <CategoriesMenu isMobile={isMobile} windowSize={WindowSize.Mobile} additionalCategories={additionalCategories} mainCategories={mainCategories} tags={tags} />
      </Flex>
    ),
    [MENU_TYPE.DESKTOP_HOME]: () => (
      <>
        {isHome && (
          <HStack justifyContent="space-between" mb="20px">
            <HStack>
              {/*<Temperature />*/}
              <Flex fontSize="12px">
                <Text color="#000" textTransform="capitalize" fontWeight="700">
                  {moment().format("dddd,")}&nbsp;
                </Text>
                <Text color="gray">
                  {moment().format("D MMM Y")}
                </Text>
              </Flex>
            </HStack>
            <HStack>
              <Link href="https://www.facebook.com/rogerpublishing/" isExternal>
                <FacebookIcon />
              </Link>
              <Link href="https://www.instagram.com/mamaginekolog/" isExternal>
                <InstagramIcon />
              </Link>
            </HStack>
          </HStack>
        )}
        <Grid w="100%" templateColumns={isHome ? "30% 40% 30%" : "10% 30% 20% 40%"} justifyContent="space-between" alignItems="center"
              gridRowGap="20px" gridTemplateAreas={isHome ? `"search logo additionalMenu"
                                          "mainMenu mainMenu mainMenu"` :
          `"logo search additionalMenu mainMenu"`}>
          <Box gridArea="search" alignItems="center" justifyContent="flex-start">
            <Search autoFocus={isAutofocus} searchQuery={searchQuery} onSearch={setSearchQuery} />
          </Box>
          <Box gridArea="logo">
            <GatsbyLink to={baseUrl}><Image src={isHome ? logoImg : logoVerticalImg} minH="30px" alt="" m="0 auto" /></GatsbyLink>
          </Box>
          <Cards isHome={isHome}/>
          <HStack gridArea="mainMenu" zIndex="9" position={isHome ? 'relative' : 'static'} borderY={isHome ? "1px solid #80A0C7" : ''} pt="12px" pb="12px" fontSize="14px" justifyContent="space-between">
            {
              isHome && (
                <Flex maxW="650px" w="100%" fontWeight="700" borderRight="1px solid #C0CEDB" pr="40px" justifyContent="space-between" pt="12px" pb="12px" fontSize="14px" >
                  {mainCategoriesOrdered.map(({ name, slug, categoryConfig }) => (
                    <Flex justifyContent={{sm:'center', xl: 'flex-start'}} flexDirection={{base: 'column', xl: 'row'}} key={slug} alignItems="center" as={GatsbyLink} to={generateBlogCategoryUrl(slug)}>
                      {categoryConfig.logo && (<Image src={categoryConfig?.logo?.sourceUrl} alt="" mr={{base: 0, xl: "10px"}}  maxH={name? "24px": "50px"} />)}
                      {name}
                    </Flex>
                  ))}
                </Flex>
              )
            }
            <CategoriesMenu isMobile={isMobile} windowSize={WindowSize.Desktop} additionalCategories={additionalCategories} mainCategories={mainCategories} tags={tags} />
          </HStack>
        </Grid>
        {
          searchQuery.trim() !== '' && searchQuery.length > 2 && (
            <SearchResultPage searchQuery={searchQuery} onSearch={setSearchQuery} isMobile={isMobile} />
          )
        }
      </>
    ),
    [MENU_TYPE.DESKTOP_NOT_HOME]: () => (
      <>
        <HStack gridArea="mainMenu" zIndex="9" spacing="40px" position={isHome ? 'relative' : 'static'} borderY={isHome ? "1px solid #80A0C7" : ''} pt="12px" pb="12px" fontSize="14px" justifyContent="space-between">
          <GatsbyLink  to={baseUrl}><Image src={logoVerticalImg} minH="30px" alt="" /></GatsbyLink>
          <Box>
            <Search autoFocus={isAutofocus} searchQuery={searchQuery} onSearch={setSearchQuery} />
          </Box>
          <HStack gridArea="additionalMenu" fontWeight="700" spacing={isHome ? '10px' : '30px'} flexGrow={1 as any} justifyContent="flex-end" mr="40px">
            <MenuCard direction="row" as={Link} href="https://rpstore.pl" icon={<ShopIcon w="22px" h="22px" />}>Sklep</MenuCard>
            <MenuCard direction="row" as={Link} href="https://fundacjaernesta.pl" isExternal icon={<FundacjaIcon w="22px" h="22px" />}>Fundacja</MenuCard>
            <MenuCard direction="row" as={GatsbyLink} to="/kalkulatory" icon={<CalculatorsIcon w="26px" h="26px" />}>Kalkulatory</MenuCard>
          </HStack>
          {isHome ? (
            <HStack letterSpacing="1px" pr="50px" borderRight="1px solid #C0CEDB" fontWeight="700" spacing="30px">
              {mainCategoriesOrdered.map(({ name, slug, categoryConfig }) => (
                <Flex key={slug} as={GatsbyLink} to={generateBlogCategoryUrl(slug)} alignItems="center">
                  {categoryConfig.logo && (<Image src={categoryConfig?.logo?.sourceUrl} alt="" mx="-10px" my="-8px" maxH="50px" />)}
                  {name}
                </Flex>
              ))}
            </HStack>
          ) : <></>}

          <CategoriesMenu isMobile={isMobile} windowSize={WindowSize.Desktop} additionalCategories={additionalCategoriesNotHome} mainCategories={mainCategories} tags={tags} />
        </HStack>
        {
          searchQuery.trim() !== '' && searchQuery.length > 2 && (
            <SearchResultPage searchQuery={searchQuery} onSearch={setSearchQuery} isMobile={isMobile} />
          )
        }
      </>
    )
  };

  const resultType = useMemo(() => {
    if(isMobile === undefined) {
      return MENU_TYPE.LOADING;
    } else if (isMobile && isHome) {
      return MENU_TYPE.MOBILE_HOME;
    } else if (isMobile && !isHome) {
      return MENU_TYPE.MOBILE_NOT_HOME;
    }  else if (!isMobile && isHome) {
      return MENU_TYPE.DESKTOP_HOME;
    }   else if (!isMobile && !isHome) {
      return MENU_TYPE.DESKTOP_NOT_HOME;
    }
  }, [isMobile, isHome]);

  return (
    <Box boxShadow={isHome ? '' : '0px 4px 4px 0px #0000000F'}>
      <Container maxW="1130px" px="15px" mt={isHome ? "24px" : ''} fontFamily="Mulish" position="relative" zIndex="9">
        {typeOptions[resultType]()}
      </Container>
    </Box>
  );
};
